import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getEvent, updateEvent, deleteEvent } from '../api'
import { addVisited, removeVisited } from '../lib/identity'
import { card, input, btnPrimary } from '../lib/ui'

interface CandidateDraft {
  id?: string
  label: string
  date?: string | null
}

export default function EditEvent() {
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [loaded, setLoaded] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [candidates, setCandidates] = useState<CandidateDraft[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const d = await getEvent(id)
      setTitle(d.event.title)
      setDescription(d.event.description ?? '')
      setCandidates(d.candidates.map((c) => ({ id: c.id, label: c.label, date: c.date })))
      setLoaded(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました')
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const setLabel = (i: number, v: string) =>
    setCandidates((cs) => cs.map((c, j) => (j === i ? { ...c, label: v } : c)))
  const addRow = () => setCandidates((cs) => [...cs, { label: '' }])
  const removeRow = (i: number) => setCandidates((cs) => cs.filter((_, j) => j !== i))

  const save = async () => {
    setError('')
    const cleaned = candidates
      .map((c) => ({ id: c.id, label: c.label.trim(), date: c.date ?? null }))
      .filter((c) => c.label)
    if (!title.trim()) return setError('イベント名を入れてください')
    if (cleaned.length === 0) return setError('候補日を1つ以上入れてください')
    setSaving(true)
    try {
      await updateEvent(id, { title: title.trim(), description, candidates: cleaned })
      addVisited(id, title.trim())
      navigate(`/e/${id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
      setSaving(false)
    }
  }

  const removeEvent = async () => {
    if (!confirm('このイベントを削除しますか？\nすべての回答も消え、元には戻せません。')) return
    try {
      await deleteEvent(id)
      removeVisited(id)
      navigate('/')
    } catch (e) {
      setError(e instanceof Error ? e.message : '削除に失敗しました')
    }
  }

  if (!loaded) {
    return (
      <div className="px-4 py-16 text-center text-muted">
        {error ? <span className="text-conflict">{error}</span> : '読み込み中…'}
      </div>
    )
  }

  const labelCls = 'mb-1.5 block text-sm font-semibold text-muted'

  return (
    <div className="animate-in mx-auto max-w-2xl px-4 pb-16 pt-5">
      <div className="mb-3 text-sm">
        <a href={`/e/${id}`} className="font-medium text-muted transition hover:text-brand">
          ← イベントに戻る
        </a>
      </div>
      <h1 className="mb-5 font-display text-2xl font-bold tracking-tight text-ink">イベントを編集</h1>

      <div className={`space-y-5 p-5 ${card}`}>
        <div>
          <label className={labelCls}>イベント名</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={input} />
        </div>
        <div>
          <label className={labelCls}>メモ</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`${input} resize-none`}
          />
        </div>
        <div>
          <label className={labelCls}>候補日</label>
          <p className="mb-2 text-xs text-muted">
            既存の候補を消すと、その日の回答も一緒に消えます。
          </p>
          <div className="space-y-2">
            {candidates.map((c, i) => (
              <div key={c.id ?? `new-${i}`} className="flex gap-2">
                <input
                  value={c.label}
                  onChange={(e) => setLabel(i, e.target.value)}
                  placeholder="例：6/20(金) 19:00〜"
                  className={input}
                />
                <button
                  onClick={() => removeRow(i)}
                  className="shrink-0 rounded-lg px-2 text-muted/50 transition hover:text-conflict"
                  aria-label="候補を削除"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button onClick={addRow} className="mt-2 text-sm font-medium text-brand hover:underline">
            ＋ 候補日を追加
          </button>
        </div>

        {error && (
          <p className="rounded-xl bg-conflict-soft px-4 py-2.5 text-sm font-medium text-conflict">
            {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className={`${btnPrimary} px-8 py-3`}>
            {saving ? '保存中…' : '変更を保存'}
          </button>
          <a href={`/e/${id}`} className="px-2 text-sm font-medium text-muted hover:text-ink">
            キャンセル
          </a>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-conflict/20 bg-conflict-soft/60 p-5">
        <h2 className="mb-1 text-sm font-bold text-conflict">イベントを削除</h2>
        <p className="mb-3 text-xs text-conflict/80">
          すべての回答も消え、元には戻せません。
        </p>
        <button
          onClick={removeEvent}
          className="rounded-xl border border-conflict/30 bg-white px-5 py-2.5 text-sm font-semibold text-conflict transition hover:bg-conflict hover:text-white"
        >
          このイベントを削除
        </button>
      </div>
    </div>
  )
}
